import ModalInfo from 'components/templates/modalInfo/ModalInfo';
import React, { useEffect, useState } from 'react';
import { ISurveyDetail } from 'types/interfaces';
import './surveyModal.scss';
import { object } from 'yup';
import { useLanguage, useTranslate } from 'context/languageContext';
import InputField from 'screens/ComplaintsAndSuggestions/components/inputField/InputField';
import InputFieldArea from 'screens/ComplaintsAndSuggestions/components/inputFieldArea/InputFieldArea';
import { useLazyGetImageByIdQuery } from 'redux/services/imageService';
import Button from 'components/commons/buttons/button/Button';
import { useLazyGetSurveysQuery, usePostSurveyMutation } from 'redux/services/surveyService';
import { obtainStructureForm, obtainSurveyBody } from 'utils/SurveyUtils';
import { updateContextualMessage, updateShowLoading } from 'redux/slices/contextualSlice';
import { useDispatch } from 'react-redux';
import useSurvey from 'hooks/useSurvey/useSurvey';
import { updateSurveys } from 'redux/slices/surveySlice';
import useResponsive from 'hooks/useResponsive/useResponsive';
import StarRating from './StarRating';

interface SurveyModalProps {
  survey: ISurveyDetail;
  onClose: Function;
  stopId?: number;
  poiId?: number;
  lineId?: number;
  sublineId?: number;
  routeId?: number;
  faqId?: number;
  planId?: string;
}

export default function SurveyModal(props: SurveyModalProps) {
  const t = useTranslate();
  const [formValues, setFormValues] = useState({});
  const [renderedImage, setRenderedImage] = useState<any>();
  const [getImageById] = useLazyGetImageByIdQuery();
  const [errorsForm, setErrorsForm] = useState<any>({});
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [postSurvey] = usePostSurveyMutation();
  const dispatch = useDispatch();
  const { obtainObjectSchema } = useSurvey();
  const [getAllSurveys] = useLazyGetSurveysQuery();
  const locale = useLanguage();
  const { isMobile, isTablet, isPortrait } = useResponsive();

  useEffect(() => {
    setFormValues(obtainStructureForm(props.survey.questions));
    if (props.survey.imageId) {
      getImageById(props.survey.imageId).then((result) => {
        if (result?.data) {
          setRenderedImage(result?.data);
        }
      });
    }
  }, [props.survey]);

  const surveySchema = object(obtainObjectSchema(props.survey.questions));

  function handleSubmit() {
      surveySchema
        .validate(formValues, { abortEarly: false })
        .then(() => {
          dispatch(updateShowLoading(true));
          setErrorsForm({});
          console.log('Raw formValues: ', formValues);
          console.log('Form values to submit: ', obtainSurveyBody(
            props.survey,
            formValues,
            props.stopId,
            props.poiId,
            props.lineId,
            props.sublineId,
            props.routeId,
            props.faqId,
            props.planId,
          ));
          postSurvey(
            obtainSurveyBody(
              props.survey,
              formValues,
              props.stopId,
              props.poiId,
              props.lineId,
              props.sublineId,
              props.routeId,
              props.faqId,
              props.planId,
            ),
          ).then((result: any) => {
            if (!result.error) {
              getAllSurveys(locale)
                .then((resSurveys) => {
                  if (resSurveys.isSuccess) {
                    dispatch(updateSurveys(resSurveys.data));
                  }
                })
                .finally(() => {
                  props.onClose();
                  dispatch(
                    updateContextualMessage({
                      message: t('lb_survey_send_success'),
                      type: 'success',
                    }),
                  );
                });
            } else {
              dispatch(
                updateContextualMessage({
                  message: t('lb_survey_send_error'),
                  type: 'error',
                }),
              );
            }
          });
        })
        .catch((errors: any) => {
          console.log('Validation errors: ', errors);
          const validationErrors = {};
          errors.inner.forEach((error) => {
            validationErrors[error.path] = error.message;
          });
          setErrorsForm(validationErrors);
        })
        .finally(() => {
            dispatch(updateShowLoading(false));
        });
  }


  function renderQuestions() {
    return props.survey.questions.map((question) => {
      let content: any = [];
      switch (question.typeId) {
        //selección única
        case 1:
          content.push(
            <div key={`select-${question.id}`} className='question-section'>
              <p className='question-label'>{question.label}<i className='icon-asterisk' /></p>
              <div>
                {question.answers.map((option) => (
                  <div
                    className='radio-option'
                    key={`option-${option.id}`}
                    onChange={() => {
                      const copyValues = { ...formValues };

                      // Si por error llega el objeto completo, lo normalizamos
                      const value = typeof option === 'object' ? option.id : option;

                      copyValues[`${question.label}-${question.id}`] = Number(value);
                      setFormValues(copyValues);
                    }}
                  >
                    <input
                      type='radio'
                      id={option.id + ''}
                      name={question.label}
                      value={option.id}
                      defaultChecked={option.id === formValues[`${question.label}-${question.id}`]}
                    />
                    <label htmlFor={option.id + ''}>{option.label}</label>
                    <br />
                  </div>
                ))}
              </div>
              {errorsForm[`${question.label}-${question.id}`] && (
                <span className='error-label'>
                  {errorsForm[`${question.label}-${question.id}`]}
                </span>
              )}
            </div>,
          );

          break;
        //selección múltiple
        case 2:
          content.push(
            <div key={`selectMult-${question.id}`} className='question-section'>
              <p className='question-label'>{question.label}<i className='icon-asterisk' /></p>
              <div>
                {question.answers.map((option) => (
                  <div
                    className='box-option'
                    key={`option-${option.id}`}
                    onChange={() => {
                      const copyValues = { ...formValues };
                      if (
                        copyValues[`${question.label}-${question.id}`] &&
                        copyValues[`${question.label}-${question.id}`]?.length
                      ) {
                        if (copyValues[`${question.label}-${question.id}`]?.includes(option.id)) {
                          //caso incluye la opción
                          const optionsFiltered = copyValues[
                            `${question.label}-${question.id}`
                          ]?.filter((value) => value !== option.id);
                          copyValues[`${question.label}-${question.id}`] = optionsFiltered;
                        } else {
                          //caso añadimos la opción
                          copyValues[`${question.label}-${question.id}`].push(option.id);
                        }
                      } else {
                        copyValues[`${question.label}-${question.id}`] = [option.id];
                      }
                      setFormValues(copyValues);
                    }}
                  >
                    <input
                      type='checkbox'
                      id={option.id + ''}
                      name={question.label}
                      value={option.id}
                      defaultChecked={formValues[`${question.label}-${question.id}`]?.includes(
                        option.id,
                      )}
                    />
                    <label htmlFor={option.id + ''}>{option.label}</label>
                    <br />
                  </div>
                ))}
              </div>
              {errorsForm[`${question.label}-${question.id}`] && (
                <span className='error-label'>
                  {errorsForm[`${question.label}-${question.id}`]}
                </span>
              )}
            </div>,
          );
          break;
        //valoración (rango numérico)
        case 3:
          content.push(
            <div key={`selectMult-${question.id}`} className='question-section'>
              <p className='question-label'>{question.label}</p>
              {question.answers.map((answer) => {
                const key = `${answer.label}-${answer.id}`;
                return (
                  <div
                    key={`dropdown-${question.id}-${answer.label}-${answer.id}`}
                    style={{ display: 'flex', flexDirection: 'column', marginBottom: 16 }}
                  >
                    <label>{answer.label}</label>
                    <StarRating
                      count={answer.maxScaleValue || 5}
                      value={formValues[key]?.value || 0}
                      onChange={(newValue) => {
                        const copyValues = { ...formValues };
                        copyValues[key] = {
                          id: answer.id,
                          value: newValue,
                        };
                        setFormValues(copyValues);
                      }}
                    />
                    {errorsForm[key] && (
                      <span className='error-label'>{errorsForm[key]}</span>
                    )}
                  </div>
                );
              })}
            </div>
          );
          break;
        //texto corto
        case 4:
          content.push(
            <div key={`questionText-${question.id}`} style={{ paddingBottom: 12 }} className='question-section'>
              <p className='question-label'>{question.label}</p>
              <div className='texts-list'>
                {question.answers.map((answer) => (
                  <InputField
                    key={`inputShortText-${answer.id}`}
                    label={answer.label}
                    idLabel={`${answer.label}-${answer.id}`}
                    name={answer.label}
                    maxLength={answer.maxTextLength ? answer.maxTextLength : undefined}
                    required
                    error={errorsForm[`${answer.label}-${answer.id}`]}
                    errorLabel={errorsForm[`${answer.label}-${answer.id}`]}
                    value={formValues[`${answer.label}-${answer.id}`]?.value || ''}
                    onChange={(e) => {
                      const copyValues = { ...formValues };
                      copyValues[`${answer.label}-${answer.id}`] = {
                        id: answer.id,
                        value: e.target.value,
                      };
                      setFormValues(copyValues);
                    }}
                  />
                ))}
              </div>
            </div>,
          );
          break;
        //texto largo
        case 5:
          content.push(
            <div key={`questionTextLong-${question.id}`} style={{ paddingBottom: 12 }} className='long-text-section'>
              <p className='question-label'>{question.label}</p>
              <div className='texts-list'>
                {question.answers.map((answer) => (
                  <InputFieldArea
                    key={`inputLongText-${answer.id}`}
                    label={answer.label}
                    idLabel={`${answer.label}-${answer.id}`}
                    name={answer.label}
                    rows={10}
                    maxLength={answer.maxTextLength ? answer.maxTextLength : undefined}
                    error={errorsForm[`${answer.label}-${answer.id}`]}
                    errorLabel={errorsForm[`${answer.label}-${answer.id}`]}
                    required
                    value={formValues[`${answer.label}-${answer.id}`]?.value || ''}
                    onChange={(e) => {
                      const copyValues = { ...formValues };
                      copyValues[`${answer.label}-${answer.id}`] = {
                        id: answer.id,
                        value: e.target.value,
                      };
                      setFormValues(copyValues);
                    }}
                  />
                ))}
              </div>
            </div>,
          );
          break;
        default:
          break;
      }

      return content;
    });
  }

  return (
    <>
      <ModalInfo
        closeAction={() => setShowCheckModal(true)}
        width={isMobile || isTablet || isPortrait ? '80vw' : '1270px'}
        height='auto'
        title={props.survey.name}
        className={'contentModal'}
        style={showCheckModal ? { display: 'none' } : undefined}
      >
        {props.survey.description ? <p>{props.survey.description}</p> : null}
        {renderedImage ? (
          <div className='survey-image'>
            <img src={renderedImage?.contentFile} alt={renderedImage?.nameFile} />
          </div>
        ) : null}
        <div className='surveyModal' style={{ marginTop: 10 }}>
          <form
          className='form-survey'
          onSubmit={(e) => {
              e.preventDefault(); // 🔥 evita la recarga
              handleSubmit();
            }}
          >
            {renderQuestions()}
            <div className='row-buttons'>
            <Button
              title={t('lb_cancelar')}
              onClick={() => {
                //se presenta mensaje de posible cancelación
                setShowCheckModal(true);
              }}
              style={{
                width: '100%',
                height: 'none',
                color: '#4B5858',
                border: 'solid 1.5px #489ACA',
              }}
            />
            <Button
              type='submit'
              title={t('lb_enviar')}
              style={{ width: '100%', height: 'none', color: 'white' }}
              background='#489ACA'
            />
          </div>
        </form>
      </div>
      </ModalInfo>
      {showCheckModal ? (
        <ModalInfo
          closeAction={() => props.onClose()}
          height='auto'
          title={t('lb_survey_cancel_message')}
          className={'contentModal'}
        >
          <div className='row-buttons'>
            <Button
              title={t('lb_cancelar')}
              onClick={() => {
                setShowCheckModal(false);
              }}
              style={{
                width: '100%',
                height: 'none',
                color: '#4B5858',
                border: 'solid 1.5px #489ACA',
              }}
            />
            <Button
              type='submit'
              title={t('lb_aceptar')}
              style={{ width: '100%', height: 'none', color: 'white' }}
              background='#489ACA'
              onClick={() => props.onClose()}
            />
          </div>
        </ModalInfo>
      ) : null}
    </>
  );
}
